import { DB_TYPE } from '@/common/define/common.define';

export const REGISTERED_EXTERNAL_RESOURCE_TYPES = ['prometheus', 'elasticsearch'] as const;

export type RegisteredExternalResourceType = (typeof REGISTERED_EXTERNAL_RESOURCE_TYPES)[number];

interface ExtraServerTypeLike {
  extraType?: string | null;
}

const MAXGAUGE_TARGET_BASE_CATEGORIES = [
  'database',
  'host',
  'aws',
  'azure',
  'filesystem',
  'container',
] as const;

const MAXGAUGE_STAT_CATEGORIES_BEFORE_PROMETHEUS = [
  'database',
  ...Object.values(DB_TYPE),
  'altibase_replication',
  'host',
  'container',
] as const;

const MAXGAUGE_STAT_CATEGORIES_AFTER_PROMETHEUS = [
  'custom',
  'target',
  'filesystem',
  'aws',
  'azure',
] as const;

export const isRegisteredExternalResourceType = (
  value: unknown,
): value is RegisteredExternalResourceType => {
  return (
    typeof value === 'string' && REGISTERED_EXTERNAL_RESOURCE_TYPES.some((type) => type === value)
  );
};

export const getRegisteredExternalResourceTypes = (
  extraServers?: readonly ExtraServerTypeLike[] | null,
): RegisteredExternalResourceType[] => {
  if (!extraServers?.length) {
    return [];
  }

  const registeredTypes = new Set<RegisteredExternalResourceType>();

  for (const extraServer of extraServers) {
    const lowerType = extraServer.extraType?.toLowerCase();
    if (isRegisteredExternalResourceType(lowerType)) {
      registeredTypes.add(lowerType);
    }
  }

  return REGISTERED_EXTERNAL_RESOURCE_TYPES.filter((type) => registeredTypes.has(type));
};

export const getMaxgaugeTargetCategories = (
  registeredExternalResourceTypes: readonly RegisteredExternalResourceType[],
): string[] => {
  if (!registeredExternalResourceTypes.includes('prometheus')) {
    return [...MAXGAUGE_TARGET_BASE_CATEGORIES];
  }

  return [...MAXGAUGE_TARGET_BASE_CATEGORIES, 'prometheus'];
};

export const getMaxgaugeStatCategories = (
  registeredExternalResourceTypes: readonly RegisteredExternalResourceType[],
): string[] => {
  const prometheusCategories = registeredExternalResourceTypes.includes('prometheus')
    ? ['prometheus']
    : [];

  return [
    ...MAXGAUGE_STAT_CATEGORIES_BEFORE_PROMETHEUS,
    ...prometheusCategories,
    ...MAXGAUGE_STAT_CATEGORIES_AFTER_PROMETHEUS,
  ];
};
